import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { websiteLeadService } from '../../api/services/website-leads';
import AuthLayout from '../../components/layout-components/AuthLayout';
import { Button } from '../../components/ui/button';
import { FieldLabel, FormRequiredLegend } from '../../components/ui/field-label';
import { Input } from '../../components/ui/input';

const ContactPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = 'Name is required.';
    if (!email.trim()) next.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Enter a valid email.';
    if (!message.trim()) next.message = 'Message is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await websiteLeadService.submitContact({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      toast.success(res?.message || 'Message sent.');
      setSubmitted(true);
      setName('');
      setEmail('');
      setMessage('');
    } catch (error) {
      const fieldErrors = error?.data?.errors || error?.data?.data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const next = {};
        Object.entries(fieldErrors).forEach(([key, value]) => {
          next[key] = Array.isArray(value) ? value[0] : String(value);
        });
        setErrors(next);
      }
      toast.error(error?.data?.message || 'Unable to send message.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout
        title="Message sent"
        description="Thank you. We will reply within two working days."
        footer={
          <p className="text-center text-sm text-ink-600">
            <Link to="/login" className="font-semibold text-green-600 hover:text-green-700">
              Back to login
            </Link>
          </p>
        }
      >
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
          Your message has been delivered to the PeerMed team.
        </div>
        <Button type="button" className="mt-4 w-full" onClick={() => setSubmitted(false)}>
          Send another message
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Contact us"
      description="Clinical questions, procurement, security reviews, press — one inbox, answered by the people who build the product."
      footer={
        <p className="text-center text-sm text-ink-600">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-green-600 hover:text-green-700">
            Back to login
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <FormRequiredLegend />
        <div>
          <FieldLabel required htmlFor="contact-name">Your name</FieldLabel>
          <Input
            id="contact-name"
            className="mt-1"
            placeholder="First and last name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
        </div>
        <div>
          <FieldLabel required htmlFor="contact-email">Email</FieldLabel>
          <Input
            id="contact-email"
            className="mt-1"
            type="email"
            placeholder="you@hospital.org"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && <p className="mt-1 text-xs text-danger">{errors.email}</p>}
        </div>
        <div>
          <FieldLabel required htmlFor="contact-message">Message</FieldLabel>
          <textarea
            id="contact-message"
            className="mt-1 min-h-24 w-full rounded-md border border-parch-200 bg-white px-3 py-2.5 text-sm text-ink-900 outline-none placeholder:text-ink-400 focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
            placeholder="Tell us what you need"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {errors.message && <p className="mt-1 text-xs text-danger">{errors.message}</p>}
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Sending...' : 'Send message'}
        </Button>
        <p className="text-xs leading-relaxed text-ink-500">
          We reply within two working days. Please do not include patient-identifiable information in this form.
        </p>
      </form>
    </AuthLayout>
  );
};

export default ContactPage;
